<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import AdminLayout from './components/AdminLayout.vue'
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast/src'
import data from 'emoji-mart-vue-fast/data/all.json'
import 'emoji-mart-vue-fast/css/emoji-mart.css'
import { useRouter } from 'vue-router'
import { useAuthStore } from './stores/authStore'
import { useWorkspaceStore } from './stores/workspaceStore'

console.log('AdminGeneral component initializing')

const router = useRouter()
const { checkAuth } = useAuthStore()
const { refreshWorkspaceEmoji } = useWorkspaceStore()
const loading = ref(true)
const saving = ref(false)
const selectedEmoji = ref('')
const showSuccess = ref(false)
const showError = ref(false)
const errorMessage = ref('')
const menuOpen = ref(false)

// Create emoji index
const emojiIndex = new EmojiIndex(data)

const loadWorkspaceData = async () => {
  try {
    // First check if user is admin using auth store
    const authData = await checkAuth()
    if (!authData.isAdmin) {
      router.push('/dashboard')
      return
    }

    // Load workspace settings from our server
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/admin/workspace/settings`, {
      credentials: 'include'
    })
    
    if (response.ok) {
      const data = await response.json()
      selectedEmoji.value = data.emoji_shortcode || ':pray:'
    } else {
      // Use default if no settings found
      selectedEmoji.value = ':pray:'
    }
  } catch (error) {
    console.error('Error loading workspace data:', error)
    errorMessage.value = 'Failed to load workspace data'
    showError.value = true
  } finally {
    loading.value = false
  }
}

const selectEmoji = (emoji) => {
  selectedEmoji.value = `:${emoji.id}:`
  menuOpen.value = false
}

const getSelectedEmojiSymbol = computed(() => {
  const shortcode = selectedEmoji.value.replace(/:/g, '')
  const emoji = emojiIndex.findEmoji(shortcode)
  return emoji ? emoji.native : '🙏'
})

const saveEmoji = async () => {
  saving.value = true
  showSuccess.value = false
  showError.value = false
  
  try {
    // Check if user is still admin before saving using auth store
    const authData = await checkAuth()
    if (!authData.isAdmin) {
      throw new Error('Unauthorized: Admin access required')
    }

    // Save settings to our server
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/admin/workspace/settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        emoji_shortcode: selectedEmoji.value
      })
    })

    if (!response.ok) {
      throw new Error('Failed to save settings')
    }

    // Refresh the workspace store cache to update emoji across all components
    console.log('Refreshing workspace emoji cache...')
    await refreshWorkspaceEmoji()
    console.log('Workspace emoji cache refreshed')

    showSuccess.value = true
  } catch (error) {
    console.error('Error saving emoji settings:', error)
    errorMessage.value = error.message === 'Unauthorized: Admin access required' 
      ? 'You do not have permission to save settings' 
      : 'Failed to save settings'
    showError.value = true
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  console.log('AdminGeneral component mounted')
  await loadWorkspaceData()
})
</script>

<template>
  <AdminLayout :showStatsSidebar="false">
    <v-container fluid class="pa-6">
      <v-card elevation="3" class="rounded-lg">
        <v-card-title class="d-flex align-center pa-6 bg-gradient-primary">
          <v-icon icon="mdi-cog" class="mr-3" size="32" color="white" />
          <span class="text-h5 font-weight-bold text-white">General Settings</span>
        </v-card-title>
        
        <v-card-text class="pa-6">
          <div v-if="loading" class="d-flex justify-center align-center py-8">
            <v-progress-circular 
              indeterminate 
              color="primary" 
              size="64"
              width="6"
            />
            <span class="ml-4 text-h6 text-medium-emphasis">Loading settings...</span>
          </div>
          
          <div v-else>
            <v-alert
              v-if="showSuccess"
              type="success"
              class="mb-6"
              closable
              variant="tonal"
              border="start"
              border-color="success"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-check-circle" color="success" />
              </template>
              <div class="d-flex align-center">
                <span class="font-weight-medium">Settings saved successfully!</span>
                <v-spacer></v-spacer>
                <v-btn
                  icon="mdi-close"
                  variant="text"
                  size="small"
                  @click="showSuccess = false"
                ></v-btn>
              </div>
            </v-alert>
            
            <v-alert
              v-if="showError"
              type="error"
              class="mb-6"
              closable
              variant="tonal"
              border="start"
              border-color="error"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-alert-circle" color="error" />
              </template>
              <div class="d-flex align-center">
                <span class="font-weight-medium">{{ errorMessage }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  icon="mdi-close"
                  variant="text"
                  size="small"
                  @click="showError = false"
                ></v-btn>
              </div>
            </v-alert>

            <v-form @submit.prevent="saveEmoji">
              <v-container fluid class="pa-0">
                <v-row>
                  <v-col cols="12">
                    <div class="d-flex align-center mb-4">
                      <v-icon icon="mdi-emoticon" color="primary" class="mr-3" size="24" />
                      <h3 class="text-h6 font-weight-bold mb-0">Kudos Emoji Configuration</h3>
                    </div>
                    
                    <p class="text-body-2 text-medium-emphasis mb-6">
                      Choose the emoji that will be used when users give kudos to each other. 
                      This emoji will appear in the leaderboard and throughout the application.
                    </p>

                    <div class="mb-6">
                      <label class="text-subtitle-1 font-weight-medium mb-3 d-block">
                        Selected Emoji
                      </label>

                      <v-menu
                        v-model="menuOpen"
                        :close-on-content-click="false"
                        location="bottom"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            variant="outlined"
                            v-bind="props"
                            class="emoji-selector"
                            size="large"
                            color="primary"
                          >
                            <span class="text-h4 mr-3">{{ getSelectedEmojiSymbol }}</span>
                            <div class="d-flex flex-column align-start">
                              <span class="text-body-2 font-weight-medium">{{ selectedEmoji }}</span>
                              <span class="text-caption text-medium-emphasis">Click to change</span>
                            </div>
                            <v-icon icon="mdi-chevron-down" class="ml-4" />
                          </v-btn>
                        </template>

                        <v-card min-width="350" class="emoji-picker-card">
                          <v-card-title class="pa-4 bg-light-blue-lighten-5">
                            <v-icon icon="mdi-emoticon" class="mr-2" color="primary" />
                            Pick an Emoji
                          </v-card-title>
                          <v-card-text class="pa-0">
                            <Picker
                              :data="emojiIndex"
                              @select="selectEmoji"
                              :native="true"
                              title="Pick an emoji..."
                              emoji="point_up"
                              :show-preview="false"
                            />
                          </v-card-text>
                        </v-card>
                      </v-menu>
                    </div>
                    
                    <div class="d-flex justify-end">
                      <v-btn
                        color="primary"
                        variant="elevated"
                        type="submit"
                        :loading="saving"
                        :disabled="saving"
                        size="large"
                        prepend-icon="mdi-content-save"
                        class="px-6"
                      >
                        {{ saving ? 'Saving...' : 'Save Changes' }}
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </AdminLayout>
</template>

<style scoped>
.emoji-selector {
  min-width: 200px;
  justify-content: flex-start;
  height: 80px;
  border-radius: 12px;
}

.settings-card {
  transition: all 0.3s ease;
  border-color: #e0e0e0;
}

.settings-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-color: #1976d2;
}

.emoji-picker-card {
  border-radius: 12px;
  overflow: hidden;
}

.bg-gradient-primary {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
}

.bg-light-blue-lighten-5 {
  background-color: #e3f2fd;
}

.text-medium-emphasis {
  color: rgba(0, 0, 0, 0.6) !important;
}

.text-white {
  color: white !important;
}

.font-weight-bold {
  font-weight: 700 !important;
}

.font-weight-medium {
  font-weight: 500 !important;
}

.text-h5 {
  font-size: 1.5rem !important;
  line-height: 2rem !important;
}

.text-h6 {
  font-size: 1.25rem !important;
  line-height: 1.75rem !important;
}

.text-body-2 {
  font-size: 0.875rem !important;
  line-height: 1.25rem !important;
}

.text-caption {
  font-size: 0.75rem !important;
  line-height: 1rem !important;
}

.text-subtitle-1 {
  font-size: 1rem !important;
  line-height: 1.5rem !important;
}

.rounded-lg {
  border-radius: 12px;
}

.pa-6 {
  padding: 24px;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-3 {
  margin-bottom: 12px;
}

.mb-4 {
  margin-bottom: 16px;
}

.mb-6 {
  margin-bottom: 24px;
}

.ml-4 {
  margin-left: 16px;
}

.mr-2 {
  margin-right: 8px;
}

.mr-3 {
  margin-right: 12px;
}

.px-6 {
  padding-left: 24px;
  padding-right: 24px;
}

.py-8 {
  padding-top: 32px;
  padding-bottom: 32px;
}

.d-flex {
  display: flex;
}

.align-center {
  align-items: center;
}

.align-start {
  align-items: flex-start;
}

.justify-center {
  justify-content: center;
}

.justify-end {
  justify-content: flex-end;
}

.flex-column {
  flex-direction: column;
}
</style> 